import type { PropsWithChildren, ReactNode } from "react";
import { Box, Button, Card, Flex, Grid, HStack, IconButton, Stack, Text } from "@chakra-ui/react";
import type { CardRootProps } from "@chakra-ui/react";
import type { LucideIcon } from "lucide-react";
import { Activity, ArrowDownRight, ArrowLeft, ArrowUpRight, Layers3, Moon, Sun, X } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { AppNotification } from "../providers/notification-provider";
import { notificationEventName } from "../providers/notification-provider";

type PanelProps = PropsWithChildren<Omit<CardRootProps, "title"> & { title?: ReactNode; action?: ReactNode; icon?: LucideIcon }>;

export function Panel({ children, title, action, icon: Icon = Layers3, p = "5", ...props }: PanelProps) {
  const hasHeader = Boolean(title || action);
  return (
    <Card.Root variant="outline" size="sm" bg="bg.panel" borderColor="border.default" borderRadius="panel" overflow="hidden" {...props}>
      {hasHeader && (
        <Card.Header p="0">
          <Flex px={p} pt={p} pb="3" align="center" justify="space-between" gap="4">
            <HStack gap="2.5" minW="0">
              <Flex boxSize="28px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="rgba(99,102,241,.11)" color="brand.400">
                <Box as={Icon} boxSize="3.5" />
              </Flex>
              <Text fontWeight="650" color="fg.default" truncate>{title}</Text>
            </HStack>
            {action}
          </Flex>
        </Card.Header>
      )}
      <Card.Body p="0">
        <Box px={p} pt={hasHeader ? "0" : p} pb={p}>{children}</Box>
      </Card.Body>
    </Card.Root>
  );
}

type StatusTone = "positive" | "negative" | "warning" | "info" | "predictive" | "neutral";

export function StatusPill({ children, tone = "neutral" }: PropsWithChildren<{ tone?: StatusTone }>) {
  const styles: Record<StatusTone, { bg: string; color: string; border: string }> = {
    positive: { bg: "rgba(16,185,129,.12)", color: "status.positive", border: "rgba(16,185,129,.24)" },
    negative: { bg: "rgba(244,63,94,.12)", color: "status.negative", border: "rgba(244,63,94,.24)" },
    warning: { bg: "rgba(245,158,11,.12)", color: "status.warning", border: "rgba(245,158,11,.24)" },
    info: { bg: "rgba(59,130,246,.12)", color: "status.info", border: "rgba(59,130,246,.24)" },
    predictive: { bg: "rgba(139,92,246,.12)", color: "strategy.predictive", border: "rgba(139,92,246,.24)" },
    neutral: { bg: "bg.subtle", color: "fg.muted", border: "border.default" },
  };
  const map = styles[tone];
  return <Box as="span" display="inline-flex" alignItems="center" whiteSpace="nowrap" borderRadius="4px" px="2.5" py="1" fontSize="xs" fontWeight="700" letterSpacing=".03em" bg={map.bg} color={map.color} borderWidth="1px" borderColor={map.border}>{children}</Box>;
}

export function MetricCard({ label, value, change, positive, detail, icon: Icon = Activity }: { label: string; value: ReactNode; change?: string; positive?: boolean; detail?: string; icon?: LucideIcon }) {
  return (
    <Panel p="4">
      <Stack gap="2">
        <Flex align="center" justify="space-between" gap="3">
          <Text fontSize="xs" color="fg.muted" fontWeight="600">{label}</Text>
          <Flex boxSize="28px" flexShrink="0" align="center" justify="center" borderRadius="4px" bg="rgba(99,102,241,.11)" color="brand.400">
            <Box as={Icon} boxSize="3.5" />
          </Flex>
        </Flex>
        <Flex align="baseline" gap="2" minW="0">
          <Text fontSize={{ base: "xl", xl: "2xl" }} lineHeight="1" fontWeight="700" color="fg.default" fontVariantNumeric="tabular-nums" truncate>{value}</Text>
          {change && <HStack gap="1" color={positive ? "status.positive" : "status.negative"}><Box as={positive ? ArrowUpRight : ArrowDownRight} boxSize="3.5" /><Text fontSize="xs" fontWeight="700">{change}</Text></HStack>}
        </Flex>
        {detail && <Text fontSize="xs" color="fg.subtle">{detail}</Text>}
      </Stack>
    </Panel>
  );
}

export function PageHeader({ title, description, actions, back }: { title: string; description?: string; actions?: ReactNode; back?: { label: string; to: string } }) {
  return (
    <Flex direction={{ base: "column", md: "row" }} align={{ base: "stretch", md: "center" }} justify="space-between" gap="4" mb="6">
      <Box>
        {back && <Button asChild size="xs" variant="ghost" color="fg.muted" ml="-2" mb="1.5"><Link to={back.to}><Box as={ArrowLeft} boxSize="3.5"/>{back.label}</Link></Button>}
        <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="750" color="fg.default">{title}</Text>
        {description && <Text mt="1" color="fg.muted" fontSize="sm">{description}</Text>}
      </Box>
      {actions && <HStack gap="2" flexWrap="wrap">{actions}</HStack>}
    </Flex>
  );
}

export function SectionTitle({ children, hint }: PropsWithChildren<{ hint?: string }>) {
  return <Flex justify="space-between" align="center" gap="3" mb="3"><Text fontSize="sm" fontWeight="700" color="fg.default">{children}</Text>{hint && <Text fontSize="xs" color="fg.muted">{hint}</Text>}</Flex>;
}

export function Money({ value, signed = false }: { value: number; signed?: boolean }) {
  const text = `${signed && value > 0 ? "+" : ""}$${Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return <Text as="span" fontFamily="mono" fontVariantNumeric="tabular-nums" color={signed ? (value >= 0 ? "status.positive" : "status.negative") : "fg.default"}>{value < 0 && signed ? `-${text.replace("$", "$")}` : text}</Text>;
}

export function Percent({ value, signed = true }: { value: number; signed?: boolean }) {
  return <Text as="span" fontFamily="mono" fontVariantNumeric="tabular-nums" color={signed ? (value >= 0 ? "status.positive" : "status.negative") : "fg.default"}>{signed && value > 0 ? "+" : ""}{value.toFixed(2)}%</Text>;
}

export function DataBar({ value, max, tone = "brand" }: { value: number; max: number; tone?: "brand" | "positive" | "warning" | "negative" }) {
  const pct = Math.max(0, Math.min(100, (value / Math.max(max, 0.0001)) * 100));
  const color = tone === "positive" ? "status.positive" : tone === "warning" ? "status.warning" : tone === "negative" ? "status.negative" : "brand.500";
  return <Box h="6px" bg="bg.subtle" borderRadius="2px" overflow="hidden"><Box h="100%" w={`${pct}%`} bg={color} borderRadius="inherit" transition="width .2s ease" /></Box>;
}

export function EmptyState({ icon: Icon, title, description, action }: { icon?: LucideIcon; title: string; description: string; action?: ReactNode }) {
  return <Flex minH="220px" align="center" justify="center" textAlign="center"><Stack gap="3" align="center" maxW="420px">{Icon && <Box as={Icon} boxSize="7" color="fg.muted" />}<Text fontWeight="700">{title}</Text><Text color="fg.muted" fontSize="sm">{description}</Text>{action}</Stack></Flex>;
}

export function ThemeToggle() {
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme !== "light";
  return <IconButton aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"} size="sm" variant="ghost" color="fg.muted" onClick={() => setTheme(isDark ? "light" : "dark")}><Box as={isDark ? Sun : Moon} boxSize="4.5" /></IconButton>;
}

export function NotificationStack() {
  const [items, setItems] = useState<AppNotification[]>([]);
  useEffect(() => {
    const add = (event: Event) => {
      const notice = (event as CustomEvent<AppNotification>).detail;
      setItems((old) => [...old.filter((x) => x.key !== notice.key), notice].slice(-4));
      window.setTimeout(() => setItems((old) => old.filter((x) => x.key !== notice.key)), 4200);
    };
    const close = (event: Event) => setItems((old) => old.filter((x) => x.key !== (event as CustomEvent<string>).detail));
    window.addEventListener(notificationEventName, add);
    window.addEventListener(`${notificationEventName}-close`, close);
    return () => { window.removeEventListener(notificationEventName, add); window.removeEventListener(`${notificationEventName}-close`, close); };
  }, []);
  return (
    <Stack position="fixed" top="72px" right={{ base: "3", md: "5" }} zIndex="2000" gap="2" w={{ base: "calc(100vw - 24px)", sm: "360px" }}>
      {items.map((item) => (
        <Box
          key={item.key}
          role={item.type === "error" ? "alert" : "status"}
          aria-live={item.type === "error" ? "assertive" : "polite"}
          data-testid="notification-toast"
          bg="bg.elevated"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="4px"
          p="3.5"
          boxShadow="0 16px 50px rgba(0,0,0,.22)"
        >
          <Flex gap="3" align="start"><Box flex="1"><Text fontWeight="700" fontSize="sm" color={item.type === "error" ? "status.negative" : item.type === "success" ? "status.positive" : "fg.default"}>{item.message}</Text>{item.description && <Text fontSize="xs" color="fg.muted" mt="1">{item.description}</Text>}</Box><IconButton aria-label="Close notification" size="xs" variant="ghost" onClick={() => setItems((old) => old.filter((x) => x.key !== item.key))}><Box as={X} boxSize="3.5" /></IconButton></Flex>
        </Box>
      ))}
    </Stack>
  );
}

export function ResponsiveStatGrid({ children }: PropsWithChildren) {
  return <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(3, 1fr)", xl: "repeat(5, 1fr)" }} gap="3">{children}</Grid>;
}
